import React, { type FC, useState, useEffect } from 'react';
import { Button } from '@mui/material';
import { type Mission, type TaskTypes } from '@shared/entities/mission/entity';
import { missionRepo } from '@repositories';
import { type EntityFilter } from 'remult';
import MissionForm from './MissionForm';
import "./MissionList.scss";
import { MissionCard } from './MissionCard';

interface MissionListProps {
    filter?: EntityFilter<Mission>;
    mission: Partial<Mission>;
    missionType: TaskTypes;
}

const MissionList: FC<MissionListProps> = ({ filter, mission, missionType }) => {
    const [missions, setMissions] = useState<Mission[]>([]);
    const [isMissionFormOpen, setMissionFormOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0); // To trigger mission list refresh

    useEffect(() => {
        if (!filter) return; // If filter is not provided, do not fetch missions
        const fetchMissions = async () => {
            try {
                const filteredMissions = await missionRepo.find({
                    where: { ...filter, isArchived: false },
                    include: {
                        association: { include: { user: true } },
                        group: true,
                        opportunity: true,
                        team: true,
                        assignedManager: true,
                        creatorManager: true,
                    },
                });
                setMissions(filteredMissions);
            } catch (error) {
                console.error('Error fetching missions:', error);
            }
        };

        fetchMissions();
    }, [filter, refreshKey]);

    const handleMissionFormSuccess = () => {
        setMissionFormOpen(false);
        setRefreshKey((prev) => prev + 1); // Trigger mission list refresh
    };

    if (!filter) return null; // If filter is not provided, do not render the component

    return (
        <div className='mission-list-container'>
            <div className='mission-list-top-container'>
                <h2 className='mission-list-title'>מטלות</h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setMissionFormOpen(true)}
                >
                    מטלה חדשה
                </Button>
            </div>
            {missions.length > 0 ? (
                <div className='mission-card-container'>
                    {missions.map((mission) => (
                        <MissionCard key={mission.id} mission={mission} />
                    ))}
                </div>
            ) : (
                <p style={{ padding: "1rem" }}>לא נמצאו מטלות משוייכות</p>
            )}
            {isMissionFormOpen && (
                <MissionForm
                    open={isMissionFormOpen}
                    onClose={() => setMissionFormOpen(false)}
                    onSuccess={handleMissionFormSuccess}
                    mission={mission}
                    missionType={missionType}
                />
            )}
        </div>
    );
};

export default MissionList;
